import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import StaffLayout from "../components/StaffLayout";

type MerchantRow = {
  merchant_id: string;
  company_name: string;
  industry?: string;
  contact_email?: string;
  order_status?: "受注" | "失注";

  // merged progress fields
  merchant_type?: string;
  status?: string;
  docs_total?: number;
  docs_completed?: number;
  uploads_total?: number;
  uploads_completed?: number;
  approval_status?: "未承認" | "承認済";
  sales_persons?: string[];
};

type ProgressRow = {
  merchant_id: string;
  company_name: string;
  industry?: string;
  merchant_type?: string;
  status?: string;
  order_status?: "受注" | "失注";
  docs_total: number;
  docs_completed: number;
  uploads_total: number;
  uploads_completed: number;
  updated_at?: string;
  approval_status?: "未承認" | "承認済";
  sales_persons?: string[];
  sales_person_display?: string;
};

const BASE_URL = "/api";

function statusStyle(status: string): React.CSSProperties {
  const s = (status || "").trim();

  if (s === "承認済") {
    return {
      background: "#ecfdf5",
      border: "1px solid #bbf7d0",
      color: "#166534",
    };
  }

  if (s === "提出済（承認待ち）" || s === "提出済") {
    return {
      background: "#eff6ff",
      border: "1px solid #bfdbfe",
      color: "#1d4ed8",
    };
  }

  if (s === "完了") {
    return {
      background: "#fff1f2",
      border: "1px solid #fecdd3",
      color: "#b91c1c",
    };
  }

  return {
    background: "#f1f5f9",
    border: "1px solid #cbd5e1",
    color: "#334155",
  };
}

function pillBase(): React.CSSProperties {
  return {
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
  };
}

export default function MerchantList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<MerchantRow[]>([]);
  const [error, setError] = useState("");

  // filters
  const [q, setQ] = useState("");
  const [orderFilter, setOrderFilter] = useState<"" | "受注" | "失注">("");
  const [progressFilter, setProgressFilter] = useState<
    "" | "進行中" | "完了" | "提出済（承認待ち）" | "承認済"
  >("");
  const [salesFilter, setSalesFilter] = useState<string>("");

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("staff_google_token") || "";

        // REQUIRED: merchant list
        const merchantRes = await fetch(`${BASE_URL}/staff/merchants/`);
        if (!merchantRes.ok) {
          throw new Error("加盟店一覧の取得に失敗しました。");
        }

        const merchantsJson = await merchantRes.json();
        const merchants: MerchantRow[] = Array.isArray(merchantsJson) ? merchantsJson : [];

        // OPTIONAL: progress list
        let progressRows: ProgressRow[] = [];
        try {
          const progressRes = await fetch(`${BASE_URL}/staff/progress`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });

          if (progressRes.ok) {
            const progressJson = await progressRes.json();
            progressRows = Array.isArray(progressJson?.merchants) ? progressJson.merchants : [];
          } else {
            console.warn("Progress API skipped:", progressRes.status);
          }
        } catch (progressErr) {
          console.warn("Progress API failed, continuing with merchant list only.", progressErr);
        }

        const progressMap = new Map<string, ProgressRow>(
          progressRows.map((p) => [p.merchant_id, p])
        );

        const merged: MerchantRow[] = merchants.map((m) => {
  const p = progressMap.get(m.merchant_id);

  return {
    ...m,
    merchant_type: p?.merchant_type || undefined,
    status: p?.status || undefined,
    docs_total: p?.docs_total ?? 0,
    docs_completed: p?.docs_completed ?? 0,
    uploads_total: p?.uploads_total ?? 0,
    uploads_completed: p?.uploads_completed ?? 0,
    approval_status: p?.approval_status || "未承認",
    sales_persons: Array.isArray(p?.sales_persons) ? p?.sales_persons : [],
    order_status: m.order_status || p?.order_status || "受注",
  };
});

const unapprovedOnly = merged.filter(
  (m) => (m.approval_status || "未承認") !== "承認済"
);

setRows(unapprovedOnly);
        setLoading(false);
      } catch (e: any) {
        setError(
          e?.message ||
            "加盟店一覧の取得に失敗しました。バックエンドが起動しているか確認してください。"
        );
        setLoading(false);
      }
    };

    run();
  }, []);

  const handleDelete = async (id: string) => {
    setError("");

    const ok = window.confirm("本当に削除しますか？この操作は取り消せません。");
    if (!ok) return;

    try {
      const res = await fetch(`${BASE_URL}/staff/merchants/${id}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        setError("削除に失敗しました。");
        return;
      }

      setRows((prev) => prev.filter((r) => r.merchant_id !== id));
    } catch {
      setError("削除に失敗しました。バックエンドが起動しているか確認してください。");
    }
  };

  const handleToggleStatus = async (id: string, current: string | undefined) => {
    setError("");

    const next = current === "失注" ? "受注" : "失注";

    try {
      const res = await fetch(`${BASE_URL}/staff/merchants/${id}/order-status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_status: next }),
      });

      if (!res.ok) {
        setError("ステータス更新に失敗しました。");
        return;
      }

      setRows((prev) =>
        prev.map((r) => (r.merchant_id === id ? { ...r, order_status: next } : r))
      );
    } catch {
      setError("ステータス更新に失敗しました。バックエンドが起動しているか確認してください。");
    }
  };

  const salesCandidates = useMemo(() => {
    const set = new Set<string>();

    rows.forEach((r) => {
      (r.sales_persons || []).forEach((name) => {
        const trimmed = (name || "").trim();
        if (trimmed) set.add(trimmed);
      });
    });

    return Array.from(set).sort((a, b) => a.localeCompare(b, "ja"));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const qq = q.trim().toLowerCase();

    return rows.filter((m) => {
      const searchable = `${m.company_name || ""} ${m.merchant_id || ""}`.toLowerCase();
      const textOk = !qq || searchable.includes(qq);

      const currentOrderStatus = m.order_status || "受注";
      const orderOk = !orderFilter || currentOrderStatus === orderFilter;

      const isApproved = (m.approval_status || "未承認") === "承認済";
      const rawStatus = (m.status || "").trim();
      const isSubmitted = rawStatus.includes("提出済");
      const displayStatus = isApproved
        ? "承認済"
        : isSubmitted
        ? "提出済（承認待ち）"
        : rawStatus || "進行中";

      const progressOk = !progressFilter || displayStatus === progressFilter;

      const persons = (m.sales_persons || []).map((x) => (x || "").trim()).filter(Boolean);
      const salesOk = !salesFilter || persons.includes(salesFilter);

      return textOk && orderOk && progressOk && salesOk;
    });
  }, [rows, q, orderFilter, progressFilter, salesFilter]);

  const resetSearch = () => {
    setQ("");
    setOrderFilter("");
    setProgressFilter("");
    setSalesFilter("");
  };

  return (
    <StaffLayout title="加盟店アカウント管理" active="manageMerchants">
      <div style={{ fontWeight: 800, marginBottom: 12 }}>登録済み加盟店一覧</div>

      {loading && <div style={{ color: "#475569" }}>読み込み中...</div>}
      {error && <div style={{ color: "#b91c1c", fontWeight: 800, marginTop: 8 }}>{error}</div>}

      {!loading && !error && (
        <div style={{ marginTop: 10 }}>
          <div style={filterWrap}>
            <div style={filterGrid}>
              <div>
                <div style={filterLabel}>検索（会社名 / ID）</div>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="例：Clinic / M01234567"
                  style={inputStyle}
                />
              </div>

              <div>
                <div style={filterLabel}>受注/失注</div>
                <select
                  value={orderFilter}
                  onChange={(e) => setOrderFilter(e.target.value as "" | "受注" | "失注")}
                  style={inputStyle}
                >
                  <option value="">すべて</option>
                  <option value="受注">受注</option>
                  <option value="失注">失注</option>
                </select>
              </div>

              <div>
                <div style={filterLabel}>進捗ステータス</div>
                <select
                  value={progressFilter}
                  onChange={(e) =>
                    setProgressFilter(
                      e.target.value as "" | "進行中" | "完了" | "提出済（承認待ち）" | "承認済"
                    )
                  }
                  style={inputStyle}
                >
                  <option value="">すべて</option>
                  <option value="進行中">進行中</option>
                  <option value="完了">完了</option>
                  <option value="提出済（承認待ち）">提出済（承認待ち）</option>
                  <option value="承認済">承認済</option>
                </select>
              </div>

              <div>
                <div style={filterLabel}>営業担当者</div>
                <select
                  value={salesFilter}
                  onChange={(e) => setSalesFilter(e.target.value)}
                  style={inputStyle}
                >
                  <option value="">すべて</option>
                  {salesCandidates.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <div style={filterLabel}>　</div>
                <button type="button" style={btnSecondary} onClick={resetSearch}>
                  クリア
                </button>
              </div>
            </div>

            <div style={{ marginTop: 8, fontSize: 12, color: "#64748b", fontWeight: 800 }}>
              表示件数：{filteredRows.length} 件
            </div>
          </div>

          <div style={tableOuterWrap}>
            <div style={tableScrollArea}>
              <div style={tableMinWidth}>
                <div style={tableHeaderRow}>
  <div>会社名</div>
  <div>業種</div>
  <div>営業担当</div>
  <div>ステータス</div>
  <div>承認？</div>
  <div>操作</div>
</div>

                {filteredRows.map((m) => {
                  const isApproved = (m.approval_status || "未承認") === "承認済";
                  const rawStatus = (m.status || "").trim();
                  const isSubmitted = rawStatus.includes("提出済");
                  const displayStatus = isApproved
                    ? "承認済"
                    : isSubmitted
                    ? "提出済（承認待ち）"
                    : rawStatus || "進行中";

                  const canApprove = isSubmitted && !isApproved;

                  return (
                    <div key={m.merchant_id} style={tableBodyRow}>
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 500,
                            color: "#0b1f3b",
                            cursor: "pointer",
                            textDecoration: "underline",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          onClick={() => navigate(`/staff/merchants/${m.merchant_id}`)}
                          title="詳細を見る"
                        >
                          {m.company_name}
                        </div>
                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 12,
                            fontWeight: 800,
                            color: "#475569",
                          }}
                        >
                          ID: {m.merchant_id}
                        </div>
                      </div>

                      <div style={{ color: "#334155", fontWeight: 400 }}>{m.industry || "-"}</div>

                      <div style={{ color: "#334155", fontWeight: 700 }}>
  {(m.sales_persons || []).length > 0 ? (m.sales_persons || []).join("、") : "-"}
</div>

                      <div>
                        <span style={{ ...pillBase(), ...statusStyle(displayStatus) }}>
                          {displayStatus}
                        </span>
                      </div>

                      

                      <div>
                        <button
                          disabled={!canApprove}
                          onClick={() => {
                            if (!canApprove) return;
                            navigate(`/staff/progress/${m.merchant_id}`);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            borderRadius: 12,
                            border: isApproved
                              ? "1px solid #16a34a"
                              : canApprove
                              ? "1px solid #1d4ed8"
                              : "1px solid #cbd5e1",
                            background: isApproved
                              ? "linear-gradient(90deg, #16a34a 0%, #22c55e 100%)"
                              : canApprove
                              ? "linear-gradient(90deg, #2e5f9e 0%, #1d4ed8 100%)"
                              : "linear-gradient(90deg, #94a3b8 0%, #64748b 100%)",
                            color: "#fff",
                            fontWeight: 900,
                            cursor: canApprove ? "pointer" : "not-allowed",
                            opacity: canApprove || isApproved ? 1 : 0.85,
                          }}
                          title={
                            isApproved
                              ? "承認済みです"
                              : canApprove
                              ? "承認確認へ進む"
                              : "提出後に承認できます"
                          }
                        >
                          {isApproved ? "承認済" : "承認"}
                        </button>
                      </div>

                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        

                        <button
  style={btnInfoSmall}
  onClick={() => navigate(`/staff/progress/${m.merchant_id}`)}
>
  進捗確認
</button>

<button
  style={btnSecondarySmall}
  onClick={() => handleToggleStatus(m.merchant_id, m.order_status)}
>
  {m.order_status === "失注" ? "受注に戻す" : "失注にする"}
</button>

<button style={btnDangerSmall} onClick={() => handleDelete(m.merchant_id)}>
  削除
</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {filteredRows.length === 0 && (
            <div style={{ marginTop: 12, color: "#475569" }}>条件に一致する加盟店がありません。</div>
          )}
        </div>
      )}
    </StaffLayout>
  );
}

/* ---------------- Styles ---------------- */

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 40,
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};

const filterWrap: React.CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: 12,
  background: "#fff",
  marginBottom: 12,
};

const filterGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1.4fr 180px 180px 180px 120px",
  gap: 10,
  alignItems: "end",
};

const filterLabel: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 900,
  color: "#0f172a",
  marginBottom: 6,
};

const tableOuterWrap: React.CSSProperties = {
  width: "100%",
  background: "#fff",
  borderRadius: 14,
};

const tableScrollArea: React.CSSProperties = {
  width: "100%",
  overflowX: "auto",
  overflowY: "hidden",
  WebkitOverflowScrolling: "touch",
};

const tableMinWidth: React.CSSProperties = {
  minWidth: 1100,
};

const tableHeaderRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1.9fr 1fr 1.2fr 1.2fr 0.9fr 320px",
  gap: 10,
  padding: "10px 12px",
  fontWeight: 700,
  borderBottom: "1px solid #e2e8f0",
  color: "#0f172a",
  fontSize: 13,
};

const tableBodyRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1.9fr 1fr 1.2fr 1.2fr 0.9fr 320px",
  gap: 10,
  padding: "12px 12px",
  borderBottom: "1px solid #f1f5f9",
  alignItems: "center",
};

const btnSecondary: React.CSSProperties = {
  height: 40,
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  cursor: "pointer",
  background: "#fff",
  color: "#0f172a",
  fontWeight: 900,
};

const btnPrimarySmall: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 10,
  border: "none",
  background: "#0b1f3b",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
  fontSize: 13,
};

const btnSecondarySmall: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontWeight: 800,
  cursor: "pointer",
  fontSize: 13,
};

const btnInfoSmall: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 10,
  border: "1px solid #bfdbfe",
  background: "#eff6ff",
  color: "#1d4ed8",
  fontWeight: 800,
  cursor: "pointer",
  fontSize: 13,
};

const btnDangerSmall: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 10,
  border: "1px solid #fecaca",
  background: "#fff",
  color: "#b91c1c",
  fontWeight: 700,
  cursor: "pointer",
  fontSize: 13,
};
